import moment from "moment";
import { getCabinClassCode } from "./utils";

const normalizeStatus = (status) => `${status || ""}`.trim().toUpperCase();
const normalizeTime = (value) => (value ? moment.utc(value).format("YYYY-MM-DD HH:mm") : "");

const isRoundTripType = (type) => {
	const normalizedType = `${type || ""}`.trim().toLowerCase();
	return normalizedType === "round" || normalizedType === "roundtrip";
};

const getSegmentSignature = (segments = []) =>
	segments
		.map((segment) => {
			const departureCode = segment?.departureCode || segment?.origin || "";
			const arrivalCode = segment?.arrivalCode || segment?.destination || "";
			const airlineCode = segment?.airlineCode || segment?.carrier || "";
			const flightNumber = segment?.flightNumber || "";
			const departureTime = segment?.departureTime || segment?.depDateTimeStr || segment?.depTime || "";

			return [airlineCode, flightNumber, departureCode, arrivalCode, normalizeTime(departureTime)].join("|");
		})
		.join("::");

const getApprovalSearchContext = ({ approvalId, type, departureCode, arrivalCode, departureTime, returnDate, cabinClass }) => {
	if (!approvalId || !departureCode || !arrivalCode || !departureTime || !cabinClass) {
		return null;
	}

	const query = [
		`fromCity=${departureCode}`,
		`from=${departureCode}`,
		`fromCode=${departureCode}`,
		`toCity=${arrivalCode}`,
		`to=${arrivalCode}`,
		`toCode=${arrivalCode}`,
		`date=${moment.utc(departureTime).startOf("day").valueOf()}`,
		`adult=1`,
		`child=0`,
		`infant=0`,
		`cabinClass=${getCabinClassCode(cabinClass)}`,
		`approvalId=${approvalId}`,
	];

	if (isRoundTripType(type)) {
		if (!returnDate) {
			return null;
		}
		query.splice(7, 0, `returnDate=${moment.utc(returnDate).startOf("day").valueOf()}`);
		return `/app/travel/flights/round?${query.join("&")}`;
	}

	return `/app/travel/flights/oneway?${query.join("&")}`;
};

export const isFlightApprovalReadyToBook = ({ approvalRequest, status, departureTime }) => {
	if (!approvalRequest || !departureTime) {
		return false;
	}

	return normalizeStatus(status) === "APPROVED" && moment(departureTime).isAfter(moment().add(1, "days"));
};

export const getFlightApprovalSearchUrl = (booking = {}) => {
	return getApprovalSearchContext({
		approvalId: booking?.id,
		type: booking?.type,
		departureCode: booking?.departureCode,
		arrivalCode: booking?.arrivalCode,
		departureTime: booking?.departureTime,
		returnDate: booking?.returnDate,
		cabinClass: booking?.cabinClass,
	});
};

export const getFlightApprovalSearchUrlFromRequestData = ({ approvalId, requestData = {} }) => {
	const { fare = {}, flight = {}, type } = requestData || {};
	const onwardSegment = flight?.onwardSegments?.[0] || flight?.segments?.[0];
	const returnSegment = flight?.returnSegments?.[0];

	return getApprovalSearchContext({
		approvalId,
		type,
		departureCode: onwardSegment?.departureCode,
		arrivalCode: onwardSegment?.arrivalCode,
		departureTime: onwardSegment?.departureTime,
		returnDate: returnSegment?.departureTime,
		cabinClass: fare?.cabinClass || flight?.selectedFare?.cabinClass || flight?.cabinClass,
	});
};

export const getApprovalPinnedFlightCopy = (approval = {}) => {
	return {
		title: "Approved flight pinned first",
		description: "You may still compare and book another option within this limit.",
	};
};

export const getApprovedFlightMatchIndex = ({ approval, flights = [] }) => {
	const requestFlight = approval?.request?.data?.flight;
	if (!requestFlight || flights.length === 0) {
		return -1;
	}

	const approvalOnwardSignature = getSegmentSignature(requestFlight?.onwardSegments || requestFlight?.segments || []);
	const approvalReturnSignature = getSegmentSignature(requestFlight?.returnSegments || []);

	return flights.findIndex((flight) => {
		const flightOnwardSignature = getSegmentSignature(flight?.onwardSegments || flight?.segments || []);
		const flightReturnSignature = getSegmentSignature(flight?.returnSegments || []);

		return flightOnwardSignature === approvalOnwardSignature && flightReturnSignature === approvalReturnSignature;
	});
};
